import React from 'react'
import { RichText, XP_REQUEST_MODE, type ComponentProps, type ExtendedRichTextData, type PartData } from '@enonic/react-components'
import ContactFormPrototype from '/react4xp/components/prototype/ContactForm'
import type { ContactFormData, ContactFormLabels } from '/react4xp/components/prototype/ContactForm/types'

/**
 * Adapter for the styled prototype Contact Form: the markup and styling all come
 * from the prototype component, this View supplies the Enonic data, owns the
 * reCAPTCHA widget, and posts to the contact-form service.
 */
type ContactFormPartData = {
	// Which of the part's two form sources the editor picked: 'enonic' for the form
	// below, 'superoffice' for a pasted SuperOffice snippet rendered in its place.
	formSource?: string
	superOfficeScript?: string
	submitUrl?: string
	contentId?: string
	componentPath?: string
	heading?: string
	// `resolveLink` returns either undefined or a complete link.
	privacyLink?: { label: string; url: string; isExternal: boolean }
	disclaimerMessage?: ExtendedRichTextData
	// Only set when BOTH reCAPTCHA keys are configured — see the processor.
	recaptchaSiteKey?: string
	language?: string
	labels?: ContactFormLabels
}

type GrecaptchaRenderParams = {
	sitekey: string
	callback?: (token: string) => void
	'expired-callback'?: () => void
	'error-callback'?: () => void
}

declare global {
	interface Window {
		grecaptcha?: {
			render: (container: HTMLElement, params: GrecaptchaRenderParams) => number
			getResponse: (widgetId?: number) => string
			reset: (widgetId?: number) => void
		}
		__i99xRecaptchaOnload?: () => void
	}
}

const RECAPTCHA_SCRIPT_ID = 'contact-form-recaptcha-script'

// Module-level so several Contact Form parts on one page share a single <script>.
// Only ever touched from inside useEffect, so it stays null in the long-lived SSR
// module instance and never leaks state between server-side renders.
let recaptchaApiPromise: Promise<void> | null = null

function loadRecaptchaApi(language?: string): Promise<void> {
	if (recaptchaApiPromise) return recaptchaApiPromise

	recaptchaApiPromise = new Promise<void>((resolve, reject) => {
		if (window.grecaptcha?.render) {
			resolve()
			return
		}
		// The onload hook has to exist before the tag is appended, or the callback
		// fires into nothing when the script comes from cache.
		window.__i99xRecaptchaOnload = resolve
		const script = document.createElement('script')
		script.id = RECAPTCHA_SCRIPT_ID
		script.src =
			'https://www.google.com/recaptcha/api.js?render=explicit&onload=__i99xRecaptchaOnload' +
			(language ? `&hl=${encodeURIComponent(language)}` : '')
		script.async = true
		script.defer = true
		script.onerror = () => reject(new Error('reCAPTCHA API failed to load'))
		document.head.appendChild(script)
	})

	return recaptchaApiPromise
}

/**
 * Runs a snippet pasted by an editor inside `container`.
 *
 * `dangerouslySetInnerHTML` parses `<script>` tags but never executes them, so each one
 * has to be re-created as a real element for the browser to fetch and run it. Attributes
 * are copied over as authored (`src`, `defer`, `async`, ids, data-*), and any inline body
 * is carried across too, so a snippet mixing markup and script behaves as it would on a
 * plain HTML page.
 */
function runSnippet(container: HTMLElement, snippet: string) {
	const template = document.createElement('template')
	template.innerHTML = snippet
	container.appendChild(template.content)

	container.querySelectorAll('script').forEach((original) => {
		const script = document.createElement('script')
		for (const { name, value } of Array.from(original.attributes)) script.setAttribute(name, value)
		script.text = original.text
		original.replaceWith(script)
	})
}

export const ContactForm = ({ component, meta, data, common }: ComponentProps<PartData>) => {
	const {
		formSource, superOfficeScript, submitUrl, contentId, componentPath, heading, privacyLink,
		disclaimerMessage, recaptchaSiteKey, language, labels,
	} = (data || {}) as ContactFormPartData

	const isSuperOffice = formSource === 'superoffice'
	const isEdit = meta?.mode === XP_REQUEST_MODE.EDIT
	// The processor only emits recaptchaSiteKey when both keys are set, so this one
	// check covers "reCAPTCHA is not configured on this site". Inside Content Studio
	// the widget is deliberately skipped: the admin vhost is usually not on Google's
	// domain allow-list, and an editor should not be able to mail the real receiver
	// from the editing frame.
	// SuperOffice ships its own captcha, so our widget is never mounted in that mode.
	const useWidget = !!recaptchaSiteKey && !isEdit && !isSuperOffice
	const containerRef = React.useRef<HTMLDivElement | null>(null)
	const widgetIdRef = React.useRef<number | null>(null)
	// Nothing to verify when reCAPTCHA is off, so the form stays submittable. Same
	// value on server and client, so this does not cause a hydration mismatch.
	const [recaptchaVerified, setRecaptchaVerified] = React.useState(!recaptchaSiteKey)

	React.useEffect(() => {
		if (!useWidget || !containerRef.current || widgetIdRef.current !== null) return
		let cancelled = false

		loadRecaptchaApi(language)
			.then(() => {
				if (cancelled || !containerRef.current || widgetIdRef.current !== null) return
				// Explicit render (rather than the .g-recaptcha auto-render) keeps Google's
				// iframe out of the DOM React reconciles, and gives each part instance its
				// own widget id and plain-closure callbacks instead of a global by name.
				widgetIdRef.current = window.grecaptcha!.render(containerRef.current, {
					sitekey: recaptchaSiteKey!,
					callback: () => setRecaptchaVerified(true),
					'expired-callback': () => setRecaptchaVerified(false),
					'error-callback': () => setRecaptchaVerified(false),
				})
			})
			.catch(() => {
				// Leave the form unverified — the submit button stays disabled.
			})

		return () => {
			cancelled = true
		}
	}, [useWidget, recaptchaSiteKey, language])

	const superOfficeRef = React.useRef<HTMLDivElement | null>(null)
	const superOfficeRanRef = React.useRef(false)

	React.useEffect(() => {
		if (!isSuperOffice || !superOfficeScript || !superOfficeRef.current || superOfficeRanRef.current) return
		// Once per mount: the snippet's own script keeps state of its own, and running it
		// twice would leave two SuperOffice forms in the container.
		superOfficeRanRef.current = true
		runSnippet(superOfficeRef.current, superOfficeScript)
	}, [isSuperOffice, superOfficeScript])

	const handleSubmit = async (values: ContactFormData) => {
		// Sent as urlencoded rather than multipart so the service can read the fields
		// straight off `request.params`.
		const body = new URLSearchParams()
		body.set('name', values.name)
		body.set('phone', values.phone || '')
		body.set('email', values.email)
		body.set('message', values.message || '')
		// Always 'true': reaching this handler proves the box was ticked, because the
		// prototype registers the consent field with react-hook-form's `required` rule
		// and RHF blocks the submit otherwise. `values.consent` cannot be trusted here
		// — the prototype derives it with `data.consent === 'true'`, but RHF reports a
		// checkbox with no `value` attribute as boolean `true`, so that comparison is
		// always false. Harmless to keep once the prototype switches to a truthy check.
		body.set('consent', 'true')
		body.set(
			'recaptchaToken',
			useWidget && widgetIdRef.current !== null ? window.grecaptcha?.getResponse(widgetIdRef.current) || '' : '',
		)
		if (contentId) body.set('contentId', contentId)
		if (componentPath) body.set('componentPath', componentPath)

		// The prototype form treats a throw as its error path, so failures must not be
		// returned as a status.
		const fail = (error: Error) => {
			if (useWidget && widgetIdRef.current !== null) {
				// Only when a widget exists — clearing this without one would leave the
				// submit button disabled with no way for the visitor to re-enable it.
				window.grecaptcha?.reset(widgetIdRef.current)
				setRecaptchaVerified(false)
			}
			throw error
		}

		let response: Response
		try {
			response = await fetch(submitUrl!, {
				method: 'POST',
				headers: { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' },
				body: body.toString(),
			})
		} catch (error) {
			return fail(error as Error)
		}
		if (!response.ok) {
			fail(new Error(`Contact form submission failed: ${response.status}`))
		}
	}

	// Called by the prototype form's "New message" button, which clears its own sent
	// state and the field values. The token from the submission that just succeeded is
	// spent — Google rejects a second siteverify of the same token, and the service
	// verifies server-side — so the widget has to be reset and a fresh challenge solved
	// before the next submission. Guarded like `fail()`: with no widget there is nothing
	// to reset, and clearing `recaptchaVerified` would disable Send with no way back.
	const handleReset = () => {
		if (!useWidget || widgetIdRef.current === null) return
		window.grecaptcha?.reset(widgetIdRef.current)
		setRecaptchaVerified(false)
	}

	// SuperOffice mode: the section keeps its heading and layout, and formSlot takes the
	// place of our fields, consent checkbox, disclaimer, reCAPTCHA box and submit row.
	// The container is left empty on the server — the snippet only runs in the browser.
	if (isSuperOffice) {
		return <ContactFormPrototype heading={heading} formSlot={<div ref={superOfficeRef} />} />
	}

	return (
		<ContactFormPrototype
			heading={heading}
			privacyLink={privacyLink && { url: privacyLink.url, isExternal: privacyLink.isExternal }}
			disclaimerMessage={
				disclaimerMessage && (
					<RichText tag="div" data={disclaimerMessage} component={component} meta={meta} common={common} />
				)
			}
			labels={labels}
			recaptchaVerified={recaptchaVerified}
			// null removes the box entirely (no reCAPTCHA configured); undefined leaves
			// the prototype's own placeholder in place (Content Studio).
			recaptchaSlot={!recaptchaSiteKey ? null : isEdit ? undefined : <div ref={containerRef} />}
			onSubmit={handleSubmit}
			onReset={handleReset}
		/>
	)
}
